import classNames from 'classnames';
import React, { type JSX } from 'react';

import './TextStack.less';

// ==============================
// TYPES
// ==============================
/** Closed semantic set. */
type TextVariant = 'body' | 'body-emphasis' | 'body-muted' | 'modal-title' | 'section-heading';

const VARIANT_TO_CLASS: Record<TextVariant, string> = {
  'section-heading': 'section-heading',
  'modal-title': 'modal-title',
  body: 'body-copy',
  'body-emphasis': 'body-emphasis-copy',
  'body-muted': 'body-muted-copy'
};

type TextProps = {
  v: TextVariant;
  children: React.ReactNode;
  /** Render text in danger color. */
  danger?: boolean;
  /** Render `<span>` instead of `<div>` when copy must stay inline. */
  inline?: boolean;
};

type TextStackProps = {
  v: 'body';
  children: React.ReactNode;
};

// ==============================
// COMPONENTS
// ==============================
/**
 * Design-system copy. Typography only — layout (indent, spacing) stays on the parent.
 * @see docs/thoughts-on-text-component.md
 */
function Text({ v, children, inline, danger }: TextProps): JSX.Element {
  const El = inline ? 'span' : 'div';
  return <El className={classNames(VARIANT_TO_CLASS[v], danger && 'text-danger')}>{children}</El>;
}

/**
 * Vertical stack of sibling copy blocks with paragraph spacing (line-height × 0.75).
 * `v="body"` fits any p-scale block: body-copy, body-emphasis-copy, body-muted-copy, or Text v="body".
 */
function TextStack({ v, children }: TextStackProps): JSX.Element {
  return (
    <div className={`TextStack TextStack--v-${v}`}>
      {children}
    </div>
  );
}

export default Text;
export { TextStack };
export type { TextProps, TextStackProps, TextVariant };
